import { format, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, KeyboardEvent, ReactNode } from "react";
import { X } from "lucide-react";
import type { Json } from "../core";
import { enumKey, enumLabel, refLabel } from "../managers/cell_utils";
import { initialsOf } from "../managers/workspace-utils";
import { widgets } from "./registry";
import { CodeEditorField } from "./CodeEditor";
import { DomainSelectorRead } from "./domain_selector/DomainSelectorField";
import { DateCellEditor, DateTimeCellEditor } from "./DatePicker";
import type { EditCellContext, ListCellContext } from "./types";
import {
    formatMeasure,
    formatMonetary,
    formatPhone,
    formatZonedDateTime,
    type QualifierRecord,
} from "./qualified/transformers";

// Read a sibling qualifier M2O record off the current row using the widget's
// `options-<x>_field` pointer. Returns null when the field/record is absent
// (e.g. Cell render with no row context) so the formatter degrades to the bare
// value. See foundation.presentation Enhancement 12.
function siblingQualifier(
    ctx: ListCellContext,
    optionKey: string,
    fallbackField?: string,
): QualifierRecord {
    const fieldName = ctx.column.options?.[optionKey] ?? fallbackField;
    if (!fieldName) return null;
    const rec = ctx.record as Record<string, unknown> | undefined;
    return (rec?.[fieldName] as QualifierRecord) ?? null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const muted = (text = "—"): ReactNode => <span className="cell-muted">{text}</span>;
const isEmpty = (v: unknown): boolean => v == null || v === "";

function fmtDate(value: unknown): string {
    try {
        return format(parseISO(String(value)), "dd MMM yyyy");
    } catch {
        return String(value);
    }
}

function fmtDateTime(value: unknown, tz: string): string {
    try {
        let iso = String(value);
        // Backend convention: *_utc datetimes are stored as NAIVE UTC strings
        // ("2026-06-10T19:58:00"). Without an explicit offset, the date parser
        // would read them as LOCAL time and the timezone conversion below
        // would double-shift. Normalize: no offset → treat as UTC.
        if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(iso) && !/(Z|[+-]\d{2}:?\d{2})$/.test(iso)) {
            iso = iso.replace(" ", "T") + "Z";
        }
        return formatInTimeZone(iso, tz || "UTC", "dd MMM yyyy, HH:mm");
    } catch {
        return String(value);
    }
}

// ─── Inline interactive widgets ─────────────────────────────────────────────

// Generic boolean toggle. Tooltip wording is field-aware:
//   - `active` field (soft-archive convention) gets the archive
//     language ("Active — click to archive" / "Archived — click
//     to restore") because that's the user-recognised vocabulary
//     for that specific field.
//   - Every other boolean uses neutral on/off language, optionally
//     personalised with the field's human label so the user knows
//     which switch they're hovering: e.g. "Mandatory — click to
//     turn off" / "Customer Visible — click to turn on".
function ActiveSwitch({
    checked,
    onChange,
    fieldName,
    fieldLabel,
    disabled,
}: {
    checked: boolean;
    onChange?: (next: boolean) => void;
    fieldName?: string;
    fieldLabel?: string;
    disabled?: boolean;
}) {
    const title = (() => {
        if (fieldName === "active") {
            return checked ? "Active — click to archive" : "Archived — click to restore";
        }
        const noun = fieldLabel?.trim() || "";
        if (noun) {
            return checked ? `${noun} — click to turn off` : `${noun} — click to turn on`;
        }
        return checked ? "On — click to turn off" : "Off — click to turn on";
    })();
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-disabled={disabled || undefined}
            disabled={disabled}
            className={`switch ${checked ? "is-on" : ""}${disabled ? " is-disabled" : ""}`}
            title={title}
            onClick={(e) => {
                e.stopPropagation();
                if (disabled) return;
                onChange?.(!checked);
            }}
        >
            <span className="switch__track">
                <span className="switch__thumb" />
            </span>
        </button>
    );
}

// Pick one of the 11 app-palette colors deterministically from the fallback
// text — same name always gets the same colour, so the avatar stays stable
// across renders. Sum-of-char-codes mod 11 gives a good spread for short
// names without needing a real hash.
function colorIndexFor(text: string): number {
    if (!text) return 0;
    let sum = 0;
    for (let i = 0; i < text.length; i++) sum = (sum + text.charCodeAt(i)) % 11;
    return sum;
}

// ─── Edit-mode editors ──────────────────────────────────────────────────────
// Each editor takes the EditCellContext from ListView and renders an input
// that lives until the user commits (Enter / blur with change) or cancels
// (Esc / blur with no change). The actual write happens via ctx.commit which
// calls onWriteField then exits edit mode in one step.

function asDisplayString(value: unknown): string {
    if (value == null) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return refLabel(value);
}

// ── "[CODE] Name" reference labels (layout option B, user-chosen 2026-07-02) ─
// Many masters declare display_name_format="[{code}] {name}" — the raw
// bracket run reads noisy in fact cells. The BACKEND keeps sending the plain
// "[CODE] Name" string; the frontend detects the pattern and renders the code
// as a small mono chip before the name (`.ref-code` in _list-view.scss).
// Labels without the [CODE] prefix render unchanged.
const REF_CODE_RE = /^\[([^\]]+)\]\s+(.+)$/;

export function RefLabelText({ label }: { label: string }): ReactNode {
    const m = REF_CODE_RE.exec(label);
    if (!m) return <>{label}</>;
    return (
        <span className="ref-label">
            <span className="ref-code">{m[1]}</span>
            {" "}
            {m[2]}
        </span>
    );
}

/**
 * Inline text/number/date editor. `inputType` chooses the HTML input control;
 * `coerce` converts the raw string from the input back to the JSON type that
 * the backend expects. Click-out + Enter commit, Escape reverts.
 */
function TextLikeEditor({
    ctx,
    inputType,
    coerce,
    step,
}: {
    ctx: EditCellContext;
    inputType: "text" | "number" | "date" | "datetime-local" | "password";
    coerce: (raw: string) => Json | null;
    step?: string;
}) {
    const [draft, setDraft] = useState<string>(() => asDisplayString(ctx.value));
    const original = useRef(asDisplayString(ctx.value));
    // Autofocus on mount + select for fast retyping (typical inline-edit UX).
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        const el = inputRef.current;
        if (el) {
            el.focus();
            if (inputType !== "date" && inputType !== "datetime-local") el.select();
        }
    }, [inputType]);

    const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            ctx.commit(coerce(draft));
        } else if (e.key === "Escape") {
            e.preventDefault();
            ctx.cancel();
        }
    };
    const onBlur = () => {
        // Commit if the value changed; otherwise just exit edit mode quietly.
        if (draft !== original.current) ctx.commit(coerce(draft));
        else ctx.cancel();
    };
    return (
        <input
            ref={inputRef}
            type={inputType}
            step={step}
            className="cell-edit-input"
            value={draft}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
            onKeyDown={onKey}
            onBlur={onBlur}
            onClick={(e) => e.stopPropagation()}
        />
    );
}

/**
 * Enum / selection editor — `<select>` populated from `fieldDef.selection`.
 * Change commits immediately (no separate Enter); Escape cancels.
 */
function EnumEditor({ ctx }: { ctx: EditCellContext }) {
    const options = ctx.field?.selection ?? [];
    const selectRef = useRef<HTMLSelectElement | null>(null);
    useEffect(() => { selectRef.current?.focus(); }, []);
    const selectedKey = enumKey(ctx.value);
    return (
        <select
            ref={selectRef}
            className="cell-edit-input"
            defaultValue={selectedKey == null ? "" : String(selectedKey)}
            onChange={(e) => ctx.commit(e.target.value || null)}
            onKeyDown={(e) => { if (e.key === "Escape") ctx.cancel(); }}
            onBlur={ctx.cancel}
            onClick={(e) => e.stopPropagation()}
        >
            {options.map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
            ))}
        </select>
    );
}

function AvatarTile({ image, fallbackText }: { image: unknown; fallbackText: string }) {
    const src = typeof image === "string" && image.length > 0
        ? (image.startsWith("data:") || image.startsWith("http") || image.startsWith("/")
            ? image
            : `data:image/png;base64,${image}`)
        : null;
    if (src) {
        return (
            <span className="avatar avatar--sq">
                <img src={src} alt="" className="avatar__img" />
            </span>
        );
    }
    const idx = colorIndexFor(fallbackText);
    return (
        <span className="avatar avatar--sq" data-color={String(idx)}>
            {initialsOf(fallbackText || "?")}
        </span>
    );
}

function Chips({ value, displayFormat }: { value: unknown; displayFormat?: string | null }) {
    const arr = Array.isArray(value) ? value : [];
    if (!arr.length) return muted();
    const shown = arr.slice(0, 3);
    const extra = arr.length - shown.length;
    return (
        <span className="cell-chips">
            {shown.map((c, i) => (
                <span key={i} className="form-tag">
                    <RefLabelText label={refLabel(c, displayFormat)} />
                </span>
            ))}
            {extra > 0 ? <span className="cell-muted">+{extra}</span> : null}
        </span>
    );
}

// Editable chips input — the write-mode counterpart to `<Chips>`. Renders the
// current values as removable pill chips (click a chip to drop it, mirroring
// the M2M picker's chip-as-remove-button affordance) followed by a text field
// that appends a new tag on Enter; Backspace on an empty field pops the last
// chip. Controlled: `value` is the current list, `onChange` receives the full
// next list on every add/remove. Targets list-valued (json array / free-tag)
// fields — relational many2many fields keep their dedicated relational picker
// for editing and only borrow this widget's read render. Chip markup matches
// `M2MCombobox` (`m2m__chip form-tag`) so it inherits the same theme styling.
function ChipsInput({
    value,
    onChange,
    placeholder,
    autoFocus,
}: {
    value: unknown;
    onChange: (next: Json) => void;
    placeholder?: string;
    autoFocus?: boolean;
}) {
    const tags = Array.isArray(value) ? value : [];
    const [draft, setDraft] = useState("");

    const addTag = () => {
        const t = draft.trim();
        if (!t) return;
        // Skip duplicates — compare against each chip's display label.
        if (tags.some((tag) => refLabel(tag) === t)) { setDraft(""); return; }
        onChange([...tags, t] as unknown as Json);
        setDraft("");
    };
    const removeAt = (idx: number) =>
        onChange(tags.filter((_, i) => i !== idx) as unknown as Json);

    return (
        <span className="m2m__chips">
            {tags.map((tag, i) => {
                const lbl = refLabel(tag);
                return (
                    <span
                        key={`${lbl}-${i}`}
                        className="m2m__chip form-tag"
                        role="button"
                        aria-label={`Remove ${lbl}`}
                        onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                    >
                        <span>{lbl}</span>
                        <X size={11} />
                    </span>
                );
            })}
            <input
                className="cell-edit-input"
                autoFocus={autoFocus}
                value={draft}
                placeholder={tags.length ? "" : (placeholder ?? "Add…")}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); addTag(); }
                    else if (e.key === "Backspace" && !draft && tags.length) { e.preventDefault(); removeAt(tags.length - 1); }
                }}
                onClick={(e) => e.stopPropagation()}
            />
        </span>
    );
}

// List-cell wrapper for `ChipsInput` — bridges the controlled component to the
// single-commit EditCellContext that ListView's inline editor expects. Holds a
// working copy of the list, commits it once when focus truly leaves the editor
// container (relatedTarget no longer inside), and cancels on Escape. This keeps
// the multi-add interaction alive without exiting edit mode on every chip.
function ChipsCellEditor({ ctx }: { ctx: EditCellContext }) {
    const [list, setList] = useState<unknown[]>(() => (Array.isArray(ctx.value) ? ctx.value : []));
    const original = useRef(JSON.stringify(Array.isArray(ctx.value) ? ctx.value : []));
    const commitIfChanged = () => {
        if (JSON.stringify(list) !== original.current) ctx.commit(list as unknown as Json);
        else ctx.cancel();
    };
    return (
        <span
            className="cell-chips"
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) commitIfChanged();
            }}
            onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); ctx.cancel(); } }}
        >
            <ChipsInput value={list} onChange={(next) => setList(Array.isArray(next) ? next : [])} autoFocus />
        </span>
    );
}

// ─── Type-fallback registrations ────────────────────────────────────────────
// Each `registerType` entry is the default rendering for a field type when no
// `widget=` override is set on the column. Mirrors the legacy Cell.tsx switch
// 1:1 — every existing list view continues to render identically.

// Boolean default render — NULL / undefined are treated as False (off
// toggle), NOT the muted em-dash. A boolean field is always either true
// or false in the user's mental model; "no value" is semantically equal
// to "off". Showing "—" makes the user think the field is missing /
// broken when the row simply hasn't had the box ticked.
//
// Branches on the field's `readonly` flag:
//   - read-only field  → plain "Yes" / "No" text (no implication of toggling)
//   - writable field   → toggle switch that writes the new value back via
//                        onWriteField, so the user can flip it inline
widgets.registerType("boolean", {
    key: "type:boolean",
    listColWidthHint: 64,
    render: ({ value, field, column, onWriteField }) => {
        const checked = Boolean(value);  // null / undefined / "" → false
        if (field?.readonly) return <span>{checked ? "Yes" : "No"}</span>;
        return (
            <ActiveSwitch
                checked={checked}
                fieldName={column.name}
                fieldLabel={column.label}
                onChange={(next) => onWriteField?.(column.name, next as unknown as Json)}
            />
        );
    },
});

const numericRender = ({ value }: ListCellContext): ReactNode =>
    isEmpty(value) ? muted() : <span>{typeof value === "number" ? value.toLocaleString() : String(value)}</span>;
const coerceInt = (raw: string): Json | null => {
    const t = raw.trim();
    if (!t) return null;
    const n = parseInt(t, 10);
    return Number.isNaN(n) ? null : n;
};
const coerceDecimal = (raw: string): Json | null => {
    const t = raw.trim();
    if (!t) return null;
    const n = parseFloat(t);
    return Number.isNaN(n) ? null : n;
};
widgets.registerType("integer", {
    key: "type:integer",
    listColWidthHint: 88,
    render: numericRender,
    renderEdit: (ctx) => <TextLikeEditor ctx={ctx} inputType="number" coerce={coerceInt} step="1" />,
});
widgets.registerType("decimal", {
    key: "type:decimal",
    listColWidthHint: 100,
    render: numericRender,
    renderEdit: (ctx) => <TextLikeEditor ctx={ctx} inputType="number" coerce={coerceDecimal} step="any" />,
});

widgets.registerType("date", {
    key: "type:date",
    listColWidthHint: 130,
    render: ({ value }) => (isEmpty(value) ? muted() : <span>{fmtDate(value)}</span>),
    renderEdit: (ctx) => (
        <DateCellEditor value={ctx.value} commit={ctx.commit} cancel={ctx.cancel} />
    ),
});

widgets.registerType("datetime", {
    key: "type:datetime",
    listColWidthHint: 160,
    render: ({ value, timezone }) =>
        isEmpty(value) ? muted() : <span>{fmtDateTime(value, timezone ?? "UTC")}</span>,
    renderEdit: (ctx) => (
        <DateTimeCellEditor value={ctx.value} commit={ctx.commit} cancel={ctx.cancel} />
    ),
});

widgets.registerType("enum", {
    key: "type:enum",
    listColWidthHint: 110,
    render: ({ value, field, column }) => {
        if (isEmpty(value)) return muted();
        const label = enumLabel(value, field?.selection);
        const asBadge = column.widget === "badge" || column.widget === "pill";
        return asBadge ? <span className="pill pill--neutral">{label}</span> : <span>{label}</span>;
    },
    renderEdit: (ctx) => <EnumEditor ctx={ctx} />,
});

widgets.registerType("reference", {
    key: "type:reference",
    listColWidthHint: 180,
    render: ({ value, column }) => {
        if (isEmpty(value)) return muted();
        const asBadge = column.widget === "badge" || column.widget === "pill";
        const label = refLabel(value, column.displayFormat);
        return asBadge ? <span className="cell-chip">{label}</span> : <span><RefLabelText label={label} /></span>;
    },
});

widgets.registerType("many2many", {
    key: "type:many2many",
    listColWidthHint: 180,
    render: ({ value, column }) => <Chips value={value} displayFormat={column.displayFormat} />,
});

widgets.registerType("one2many", {
    key: "type:one2many",
    listColWidthHint: 130,
    render: ({ value }) =>
        Array.isArray(value)
            ? <span className="cell-muted">{value.length} item{value.length === 1 ? "" : "s"}</span>
            : muted(),
});

const mutedRender = ({ value }: ListCellContext) => (isEmpty(value) ? muted() : muted());
widgets.registerType("uuid", { key: "type:uuid", listColWidthHint: 100, render: mutedRender });
widgets.registerType("image", { key: "type:image", listColWidthHint: 60, render: mutedRender });
widgets.registerType("binary", { key: "type:binary", listColWidthHint: 80, render: mutedRender });

const stringRender = ({ value }: ListCellContext): ReactNode =>
    isEmpty(value) ? muted() : <span>{String(value)}</span>;
const coerceString = (raw: string): Json | null => (raw.length > 0 ? raw : null);
widgets.registerType("char", {
    key: "type:char",
    listColWidthHint: 165,
    render: stringRender,
    renderEdit: (ctx) => <TextLikeEditor ctx={ctx} inputType="text" coerce={coerceString} />,
});
widgets.registerType("text", {
    key: "type:text",
    listColWidthHint: 220,
    render: stringRender,
    renderEdit: (ctx) => <TextLikeEditor ctx={ctx} inputType="text" coerce={coerceString} />,
});
widgets.registerType("json", { key: "type:json", listColWidthHint: 180, render: stringRender });

// ─── Widget-key registrations ───────────────────────────────────────────────
// Explicit overrides addressable via `<field widget="...">` in the DSL.

// `password_mask` — masked secret value. Read mode shows fixed dots (never the
// value); list inline-edit is a type="password" input. (The form editor adds a
// show/hide eye toggle — see PasswordMaskField in FormSheet.) For write-only
// fields like a new password in an admin wizard.
const passwordRender = ({ value }: ListCellContext): ReactNode =>
    isEmpty(value) ? muted() : <span>••••••••</span>;
widgets.registerWidget("password_mask", {
    key: "password_mask",
    listColWidthHint: 165,
    render: passwordRender,
    renderEdit: (ctx) => <TextLikeEditor ctx={ctx} inputType="password" coerce={coerceString} />,
});

// `active_switch` — kept as an explicit alias for any consumer that wants the
// switch render regardless of the field's readonly flag. The type-boolean
// default already renders the switch for writable booleans, so most callers
// don't need this widget.
widgets.registerWidget("active_switch", {
    key: "active_switch",
    listColWidthHint: 64,
    render: ({ value, column, onWriteField }) => (
        <ActiveSwitch
            checked={Boolean(value)}
            fieldName={column.name}
            fieldLabel={column.label}
            onChange={(next) => onWriteField?.(column.name, next as unknown as Json)}
        />
    ),
});

// `avatar_title` — single-cell render with the avatar tile inline-prefixing
// the title text. Source field for the avatar declared via
// `option-avatar-field="image_avatar"`. Same column, same cell — no
// table-layout surprises from a synthetic decoration column.
widgets.registerWidget("avatar_title", {
    key: "avatar_title",
    // Baseline column width — fits a typical avatar + title ("Alfred
    // Pennyworth (Wayne Demo)" type strings). Without a hint the title
    // slot becomes the only flex column on the row and absorbs ALL
    // leftover width on a wide screen, making it dominate the table.
    // With every column carrying a hint, `table-layout: fixed` +
    // `.list-table { min-width: 100% }` scales every column
    // proportionally to fit the page width — no single-column
    // hot-spot.
    listColWidthHint: 240,
    render: ({ value, record, column }) => {
        const title = isEmpty(value) ? "" : refLabel(value);
        const avatarField = column.options?.avatar_field;
        const image = avatarField ? record[avatarField] : null;
        return (
            <span className="avatar-title">
                <AvatarTile image={image} fallbackText={title} />
                <span className="avatar-title__text">{title || muted()}</span>
            </span>
        );
    },
});

// `chips` — list-of-tags widget. Read mode shows the values as pill chips (the
// same `<Chips>` rail the many2many type render uses). Edit mode is an inline
// tag input (`ChipsInput`): existing chips are click-to-remove, a trailing text
// field appends a new tag on Enter. For relational many2many fields this widget
// supplies the READ render only — the form keeps `M2MCombobox` (itself an
// editable chip rail backed by a related-record search) for editing, since you
// pick relations rather than free-type them. For non-relational list fields
// (json arrays / free tags) the form routes edit mode here too (see FormSheet
// FieldEditor). Inline list-cell editing flows through `ChipsCellEditor`.
widgets.registerWidget("chips", {
    key: "chips",
    listColWidthHint: 180,
    render: ({ value }) => <Chips value={value} />,
    renderEdit: (ctx) => <ChipsCellEditor ctx={ctx} />,
});

// ─── Qualified-value widgets (Enhancement 12) ────────────────────────────────
// READ-mode renders. A scalar value is rendered alongside a qualifier resolved
// from a sibling Reference field named by `options-<x>_field`. Edit-mode is
// handled in FormSheet's FieldEditor (it needs the live draft + sibling picker).
// All four degrade to the bare value when the qualifier record is absent.

// `measure` — value + res.uom → "12.5 kg". Sibling via `options-uom_field`.
widgets.registerWidget("measure", {
    key: "measure",
    listColWidthHint: 120,
    render: (ctx) =>
        isEmpty(ctx.value) ? muted() : <span>{formatMeasure(ctx.value, siblingQualifier(ctx, "uom_field"))}</span>,
});

// `monetary` — value + res.currency → "$4,500.00". Sibling via
// `options-currency_field` (defaults to `currency_id`, matching fields.Monetary).
widgets.registerWidget("monetary", {
    key: "monetary",
    listColWidthHint: 120,
    render: (ctx) =>
        isEmpty(ctx.value)
            ? muted()
            : <span>{formatMonetary(ctx.value, siblingQualifier(ctx, "currency_field", "currency_id"))}</span>,
});

// `datetime_tz` — instant + res.timezone → wall-clock in the zone. Sibling via
// `options-timezone_field`; absent that, the user/session timezone (ctx.timezone).
widgets.registerWidget("datetime_tz", {
    key: "datetime_tz",
    listColWidthHint: 180,
    render: (ctx) =>
        isEmpty(ctx.value)
            ? muted()
            : <span>{formatZonedDateTime(ctx.value, siblingQualifier(ctx, "timezone_field"), ctx.timezone)}</span>,
});

// `phone` — digits + res.country dialing code → "+91 …". Sibling via
// `options-country_field`.
widgets.registerWidget("phone", {
    key: "phone",
    listColWidthHint: 150,
    render: (ctx) =>
        isEmpty(ctx.value) ? muted() : <span>{formatPhone(ctx.value, siblingQualifier(ctx, "country_field"))}</span>,
});

// `code` — syntax-highlighted source viewer (CodeMirror 6). READ-mode render;
// edit mode is handled in FormSheet's FieldEditor (same surface, editable).
// Language from `option-language` (xml | json); anything else renders plain.
// First consumer: ir.application.view `arch`. Heavy for list cells — intended
// for form bodies; a list column declaring it still works but pays the editor
// mount per row.
widgets.registerWidget("code", {
    key: "code",
    render: (ctx) => (
        <CodeEditorField value={ctx.value} readOnly language={ctx.column.options?.language} />
    ),
});

// `domain_selector` — visual EDE-domain builder (foundation.presentation Enh 13).
// Read mode renders a compact "<n> conditions" chip; the form editor mounts the
// builder dialog via FieldEditor's `domain_selector` branch (see FormSheet).
widgets.registerWidget("domain_selector", {
    key: "domain_selector",
    render: (ctx) => DomainSelectorRead(ctx),
});

// `badge` / `pill` — boolean-keyed widget override. Today the type registrations
// for enum / reference inspect `column.widget` themselves, so this widget key
// resolves to the underlying type render (no override needed). Reserved for a
// future explicit registration if the badge styling diverges.

// Export so unit tests can call into the builtin renderers directly, and so the
// form editor can reuse the controlled chips input in persistent edit mode.
export { ActiveSwitch, AvatarTile, Chips, ChipsInput, fmtDate, fmtDateTime };

// Tiny no-op so importers can rely on the side-effect: `import "./widgets"` ➜
// every builtin runs registerWidget / registerType once.
export function registerBuiltinWidgets(): void {
    // Side-effect is the module-scope registration above. This function is a
    // documentation hook — having a named function makes it clear at import
    // sites WHAT is being pulled in.
}
